import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown } from "lucide-react";
import SEO from "@/components/SEO";
import Marquee from "@/components/Marquee";
import ProcessFlow from "@/components/ProcessFlow";
import { Container, Eyebrow, MaskedLines, Reveal, SectionHead } from "@/components/Motion";
import { APPROACH_STEPS, IMAGES, INDUSTRIES, MARQUEE_ITEMS, OM_SERVICE, SERVICES, WHY_EVovLEX } from "@/data/content";

const btnPrimary =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-cyan-500/30";
const btnGhostLight =
  "inline-flex items-center justify-center gap-2 rounded-lg border border-slate-600 bg-white/5 px-6 py-3.5 text-sm font-semibold text-white backdrop-blur transition-all duration-300 hover:border-cyan-400/60 hover:text-cyan-300";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] items-center overflow-hidden bg-navy-950 grain">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={IMAGES.hero}
          alt="Abstract technology network visual"
          className="h-full w-full object-cover opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/80 to-navy-950/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-transparent to-navy-950/70" />
      </motion.div>
      <div className="absolute inset-0 grid-lines-dark" aria-hidden="true" />

      <Container className="relative pb-24 pt-36">
        <Reveal delay={0.05} y={12}>
          <p className="inline-flex items-center gap-3 rounded-full border border-slate-700/80 bg-navy-900/60 px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-cyan-300 backdrop-blur" data-testid="hero-tagline">
            <span className="h-1.5 w-1.5 animate-pulse-soft rounded-full bg-cyan-400" />
            Innovative. Secure. Scalable. Future-Ready.
          </p>
        </Reveal>

        <MaskedLines
          lines={[
            "Empowering Businesses",
            { node: <span>Through <span className="text-gradient-brand">Technology</span></span> },
          ]}
          className="mt-8 max-w-4xl font-display text-5xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl"
        />

        <Reveal delay={0.55}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg" data-testid="hero-subtext">
            EVOVLEX IT delivers innovative technology solutions designed to help businesses transform, secure, and
            scale in an increasingly digital world.
          </p>
        </Reveal>

        <Reveal delay={0.7}>
          <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
            <Link to="/services" data-testid="hero-explore-services-button" className={btnPrimary}>
              Explore Our Services
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" data-testid="hero-talk-to-expert-button" className={btnGhostLight}>
              Talk to an Expert
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.9}>
          <div className="mt-16 flex flex-wrap gap-x-8 gap-y-3 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
            {["Technology", "Security", "Innovation"].map((p) => (
              <span key={p} className="flex items-center gap-2">
                <span className="h-1 w-1 rotate-45 bg-orange-500" />
                {p}
              </span>
            ))}
          </div>
        </Reveal>
      </Container>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-slate-500 md:flex"
        aria-hidden="true"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ChevronDown className="h-4 w-4 animate-bounce" />
      </motion.div>
    </section>
  );
}

function TechForward() {
  return (
    <section data-testid="tech-forward-section" className="relative overflow-hidden bg-white py-20 lg:py-32">
      <Container>
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <SectionHead
              eyebrow="Why Technology Matters"
              title="Technology That Moves Your Business Forward"
            />
            <Reveal delay={0.2}>
              <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg">
                At EVOVLEX IT, we believe technology should do more than support your business—it should accelerate
                it. We design and implement reliable, scalable and secure technology solutions that improve
                efficiency, strengthen security and create new opportunities for growth.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <Link
                to="/services"
                data-testid="tech-forward-explore-button"
                className="group mt-8 inline-flex items-center gap-2 font-semibold text-evo-blue transition-colors hover:text-cyan-600"
              >
                Explore Our Services
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="relative">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl shadow-navy-950/20">
              <img
                src={IMAGES.techForward}
                alt="Enterprise technology infrastructure visual"
                className="aspect-[4/3] w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-navy-950/40 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-5 -left-5 hidden rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-xl sm:block">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Focus</p>
              <p className="mt-1 font-display text-sm font-bold text-slate-900">
                Transform <span className="text-evo-orange">·</span> Secure <span className="text-evo-orange">·</span> Scale
              </p>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

function CoreServices() {
  return (
    <section data-testid="core-services-section" className="bg-slate-50 py-20 lg:py-32">
      <Container>
        <SectionHead
          eyebrow="What We Do"
          title="Comprehensive Solutions for a Digital World"
          sub="EVOVLEX IT provides end-to-end technology services designed to meet the evolving requirements of businesses."
        />

        <Reveal delay={0.15}>
          <p className="mt-14 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-cyan-600">
            Technology Solutions
          </p>
        </Reveal>
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={0.05 * (i % 4)} className="h-full">
              <Link
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-cyan-500/50 hover:shadow-2xl hover:shadow-blue-600/10"
              >
                <span className="absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 bg-gradient-to-r from-blue-600 to-cyan-400 transition-transform duration-500 group-hover:scale-x-100" />
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-evo-blue transition-colors duration-300 group-hover:bg-evo-blue group-hover:text-white">
                  <s.icon className="h-6 w-6" strokeWidth={1.8} />
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-500">{s.short}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-evo-blue">
                  Learn More
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-16 flex items-center gap-4">
            <span className="h-px flex-1 bg-slate-200" />
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-evo-orange">
              Operational & Workforce Solutions
            </p>
            <span className="h-px flex-1 bg-slate-200" />
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <Link
            to={`/services/${OM_SERVICE.slug}`}
            data-testid="service-card-om-manpower"
            className="group relative mt-8 block overflow-hidden rounded-2xl border border-slate-800 bg-gradient-to-br from-navy-900 via-navy-950 to-navy-950 p-8 shadow-2xl lg:p-12"
          >
            <div className="absolute inset-0 grid-lines-dark" aria-hidden="true" />
            <div
              className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-orange-500/10 blur-[80px]"
              aria-hidden="true"
            />
            <div className="relative grid items-center gap-8 lg:grid-cols-[1fr_auto]">
              <div>
                <span className="inline-flex rounded-full bg-gradient-to-r from-orange-500 to-amber-500 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-white">
                  Operational & Workforce
                </span>
                <h3 className="mt-4 font-display text-2xl font-bold text-white sm:text-3xl">{OM_SERVICE.title}</h3>
                <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-orange-300">{OM_SERVICE.tagline}</p>
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">{OM_SERVICE.short}</p>
              </div>
              <span className="inline-flex items-center gap-2 justify-self-start rounded-lg border border-orange-500/40 px-6 py-3.5 text-sm font-semibold text-orange-300 transition-all duration-300 group-hover:bg-orange-500 group-hover:text-white lg:justify-self-end">
                Explore O&M Services
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </div>
          </Link>
        </Reveal>
      </Container>
    </section>
  );
}

function Approach() {
  return (
    <section data-testid="approach-section" className="relative overflow-hidden bg-navy-950 py-20 lg:py-32 grain">
      <div className="absolute inset-0 grid-lines-dark" aria-hidden="true" />
      <div className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-blue-600/15 blur-[110px]" aria-hidden="true" />
      <Container className="relative">
        <SectionHead
          dark
          center
          eyebrow="How We Work"
          title="Our Approach"
          sub="We begin by understanding your business challenges, develop the right technology strategy, implement the solution and continuously optimize it for future growth."
        />
        <div className="mt-14">
          <ProcessFlow steps={APPROACH_STEPS} dark testid="approach-process" />
        </div>
      </Container>
    </section>
  );
}

function IndustriesSection() {
  return (
    <section data-testid="industries-section" className="bg-white py-20 lg:py-32">
      <Container>
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <SectionHead eyebrow="Industries" title="Technology Solutions Across Industries" />
          <Reveal delay={0.2}>
            <Link
              to="/industries"
              data-testid="industries-explore-button"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-900 transition-all duration-300 hover:border-evo-blue hover:text-evo-blue"
            >
              Explore Industries
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {INDUSTRIES.map((ind, i) => (
            <Reveal key={ind.title} delay={0.05 * (i % 4)} className="h-full">
              <div
                data-testid={`industry-card-${ind.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group h-full rounded-xl border border-slate-200 bg-slate-50/60 p-6 transition-all duration-300 hover:border-cyan-500/40 hover:bg-white hover:shadow-lg hover:shadow-blue-600/5"
              >
                <ind.icon className="h-6 w-6 text-evo-blue transition-colors group-hover:text-cyan-600" strokeWidth={1.8} />
                <h3 className="mt-4 font-display text-base font-semibold text-slate-900">{ind.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{ind.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

function WhySection() {
  const spans = ["md:col-span-3", "md:col-span-3", "md:col-span-2", "md:col-span-2", "md:col-span-2", "md:col-span-6"];
  return (
    <section data-testid="why-section" className="bg-slate-50 py-20 lg:py-32">
      <Container>
        <SectionHead eyebrow="The EVOVLEX Difference" title="Why Choose EVOVLEX IT?" />
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-6">
          {WHY_EVovLEX.map((w, i) => (
            <Reveal key={w.title} delay={0.06 * i} className={spans[i]}>
              <div
                data-testid={`why-card-${w.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`group flex h-full flex-col rounded-2xl border p-8 transition-all duration-500 ${
                  i === 5
                    ? "flex-row items-center gap-6 border-slate-800 bg-navy-950 text-white hover:border-cyan-500/50"
                    : "border-slate-200 bg-white hover:-translate-y-1 hover:border-cyan-500/40 hover:shadow-xl hover:shadow-blue-600/5"
                }`}
              >
                <span
                  className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${
                    i === 5 ? "bg-cyan-500/10 text-cyan-400" : "bg-blue-50 text-evo-blue"
                  }`}
                >
                  <w.icon className="h-6 w-6" strokeWidth={1.8} />
                </span>
                <div>
                  <h3 className={`font-display text-lg font-semibold ${i === 5 ? "text-white" : "mt-5 text-slate-900"}`}>
                    {w.title}
                  </h3>
                  <p className={`mt-2 text-sm leading-relaxed ${i === 5 ? "text-slate-400" : "text-slate-500"}`}>
                    {w.desc}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

function OmFeature() {
  return (
    <section data-testid="om-feature-section" className="relative overflow-hidden bg-navy-950 py-20 lg:py-32 grain">
      <div className="absolute inset-0 grid-lines-dark" aria-hidden="true" />
      <Container className="relative">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <Reveal>
              <span className="inline-flex rounded-full bg-gradient-to-r from-orange-500 to-amber-500 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-white">
                Featured Service
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-5 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                Reliable Manpower. Efficient Operations. Better Performance.
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-5 text-base leading-relaxed text-slate-400 sm:text-lg">
                EVOVLEX IT provides professional Operation & Maintenance (O&M) manpower services to support
                industrial, infrastructure, renewable energy, electrical, mechanical, facility management and other
                operational requirements.
              </p>
            </Reveal>
            <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {OM_SERVICE.categories.map((c, i) => (
                <Reveal key={c.title} delay={0.05 * i}>
                  <div
                    data-testid={`om-category-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="flex items-center gap-3 rounded-lg border border-slate-800 bg-navy-900/60 px-4 py-3 transition-colors hover:border-orange-500/40"
                  >
                    <c.icon className="h-5 w-5 shrink-0 text-orange-400" strokeWidth={1.8} />
                    <span className="text-sm font-medium text-slate-200">{c.title.replace(" Manpower", "")}</span>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.3}>
              <Link
                to={`/services/${OM_SERVICE.slug}`}
                data-testid="om-feature-explore-button"
                className="mt-9 inline-flex items-center gap-2 rounded-lg bg-orange-500 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-orange-500/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-orange-600"
              >
                Explore O&M Manpower Services
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="relative overflow-hidden rounded-2xl border border-slate-800 shadow-2xl">
              <img
                src={IMAGES.omSolar}
                alt="Solar power plant operations and maintenance"
                className="aspect-[4/3] w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950/70 via-transparent to-transparent" />
            </div>
          </Reveal>
        </div>
        <div className="mt-16">
          <ProcessFlow steps={OM_SERVICE.approach} dark testid="om-approach-process" />
        </div>
      </Container>
    </section>
  );
}

function FinalCta() {
  return (
    <section data-testid="final-cta-section" className="relative overflow-hidden bg-white py-20 lg:py-28">
      <Container>
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-navy-950 px-8 py-16 text-center sm:px-12 lg:py-20 grain">
            <div className="absolute inset-0 grid-lines-dark" aria-hidden="true" />
            <div className="absolute -left-24 top-0 h-72 w-72 rounded-full bg-blue-600/20 blur-[100px]" aria-hidden="true" />
            <div className="absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-cyan-500/15 blur-[100px]" aria-hidden="true" />
            <div className="relative">
              <Eyebrow dark className="justify-center">Let's Build the Future Together</Eyebrow>
              <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                Ready to Transform Your Business?
              </h2>
              <p className="mx-auto mt-5 max-w-xl text-base text-slate-400 sm:text-lg">
                Let's turn your technology challenges into opportunities.
              </p>
              <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Link to="/contact" data-testid="final-cta-talk-button" className={btnPrimary}>
                  Talk to an Expert
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link to="/contact" data-testid="final-cta-contact-button" className={btnGhostLight}>
                  Contact Us
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <SEO
        title="EVOVLEX IT | Technology, Cybersecurity, Cloud & AI Solutions"
        description="EVOVLEX IT delivers secure, innovative and scalable technology solutions across software development, cybersecurity, cloud, AI, IT infrastructure and consulting."
      />
      <Hero />
      <Marquee items={MARQUEE_ITEMS} dark />
      <TechForward />
      <CoreServices />
      <Approach />
      <IndustriesSection />
      <WhySection />
      <OmFeature />
      <FinalCta />
    </>
  );
}
